import { EventType, Expression, HtmlType } from '../../types.js'
import { jsonClass, jsonProperty } from '@sumbricht/json-class-serializer'
import { Parameter } from '../../script/parameter.js'
import { ScreenEditorInput } from '../decorators/screen-editor-input.js'
import { assignCommonProperties } from '../../util/data-util.js'
import { Block } from '../block.js'
import { TextOrExpression_assumeExpression } from '../../data/text-or-code.js'
import { BlockWithDataSource } from '../common/block-with-data-source.js'
import { BlockDataSourceDefinition } from '../common/block-data-source-definition.js'
import { TextBlock } from '../other/text-block.js'

export const ChipGroupBlockAdditionTypes = {
	freeText: 'Free text',
	autoComplete: 'Auto-complete',
} as const
export type ChipGroupBlockAdditionType =
	keyof typeof ChipGroupBlockAdditionTypes

@jsonClass('ChipGroupBlock')
export class ChipGroupBlock extends Block implements BlockWithDataSource {
	@jsonProperty(BlockDataSourceDefinition)
	@ScreenEditorInput<ChipGroupBlock>({
		order: 3,
		label: '',
		compositionObj: (blocks) => blocks[0].dataSource,
	})
	dataSource: BlockDataSourceDefinition = new BlockDataSourceDefinition()

	@jsonProperty(TextOrExpression_assumeExpression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 1,
		label: 'Label',
		codeLanguage: 'ts',
		tsReturnType: () => 'string',
		editorSize: 'singleline',
		isBinding: false,
	})
	labelCode = new TextOrExpression_assumeExpression('', 'T')

	@jsonProperty(Expression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 2,
		label: () => 'Placeholder text',
		codeLanguage: 'ts',
		editorSize: 'singleline',
		tsReturnType: () => 'string',
		isBinding: false,
		isMandatory: () => false,
	})
	placeholderTextCode = new Expression()

	@jsonProperty(Expression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 4,
		label: () => 'Chip can be removed',
		codeLanguage: 'ts',
		editorSize: 'singleline',
		tsReturnType: () => 'boolean',
		isBinding: false,
		isMandatory: () => true,
	})
	canBeRemovedCode = new Expression('true')

	@jsonProperty(Expression)
	@ScreenEditorInput({
		inputType: 'code',
		order: 4,
		label: () => 'Filter expression',
		codeLanguage: 'ts',
		editorSize: 'singleline',
		tsReturnType: () => 'boolean',
		isBinding: false,
		isMandatory: () => true,
		tooltip: () => ({
			text: 'Filter expression to apply to the chip group, e.g. if the passed-in array includes items not to be shown in the chip group',
		}),
	})
	filterCode = new Expression('true')

	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'dropdown',
		order: 5,
		label: () => 'Addition type',
		options: Object.entries(ChipGroupBlockAdditionTypes),
	})
	additionType: ChipGroupBlockAdditionType = 'freeText'

	@jsonProperty(Expression)
	@ScreenEditorInput<ChipGroupBlock>({
		inputType: 'code',
		order: 6,
		tsReturnType: () => 'any[]',
		label: () => 'Addition option list',
		codeLanguage: 'ts',
		isBinding: false,
		editorSize: 'singleline',
		isVisible: (items, properties, bo) =>
			items[0].additionType == 'autoComplete',
	})
	additionOptionsCode = new Expression('[]')

	@jsonProperty(Expression)
	@ScreenEditorInput<ChipGroupBlock>({
		inputType: 'code',
		order: 6,
		tsReturnType: () => 'any',
		label: () => 'Addition option value',
		codeLanguage: 'ts',
		isBinding: false,
		editorSize: 'singleline',
		isVisible: (items, properties, bo) =>
			items[0].additionType == 'autoComplete',
	})
	additionOptionValueCode = new Expression('')

	@jsonProperty(Expression)
	@ScreenEditorInput<ChipGroupBlock>({
		inputType: 'code',
		order: 6,
		tsReturnType: () => 'string | number',
		label: () => 'Addition option label',
		codeLanguage: 'ts',
		isBinding: false,
		editorSize: 'singleline',
		isVisible: (items, properties, bo) =>
			items[0].additionType == 'autoComplete',
	})
	additionOptionLabelCode = new Expression('')

	constructor(init: Partial<ChipGroupBlock> = {}) {
		super()
		assignCommonProperties(this, init)
	}

	producePreview(): HtmlType {
		return this.placeholderTextCode.content || `Chips`
	}

	getEditorCategory(): string {
		return 'Additional Form Fields'
	}

	public getOwnVariables(
		includeDeclarationVariables: boolean,
	): Record<string, string | null> {
		const varDefs = {
			[this.dataSource.loopVariableDefinition]: this.dataSource.getItemTsType(),
			[this.dataSource.getIndexVariableName()]: 'number',
		}
		return varDefs
	}

	get allowedEventTypes(): EventType[] {
		const events = []
		if (this.additionType == 'freeText') {
			events.push({
				name: 'matChipInputTokenEnd',
				prefix: 'input',
				parameters: [
					new Parameter({
						name: 'event',
						type: 'Sys.Types.MatChipInputEvent',
						defaultExpression: '$event',
					}),
				],
			})
		} else {
			events.push({
				name: 'optionSelected',
				prefix: 'dropdown',
				parameters: [
					new Parameter({
						name: 'event',
						type: 'any',
						defaultExpression: '$event.option.value',
					}),
				],
			})
		}

		events.push(
			{ name: 'removed', prefix: 'chip' },
			{ name: 'click', prefix: 'chip' },
			{ name: 'dblclick', prefix: 'chip' },
		)

		return events
	}

	static createNewForEditor(): ChipGroupBlock {
		return new this({
			children: [
				new TextBlock({
					textCode: new TextOrExpression_assumeExpression('item', 'E'),
				}),
			],
		})
	}

	getValidationValueType(): string {
		return `${this.dataSource.getItemTsType()}[]`
	}
}
